import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Stack,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
  ListItemButton,
  Collapse,
  Typography,
  Button,
  Container,
  IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Link, LinearProgress
} from "@mui/material";
import {
  AccountCircle as AccountCircleIcon,
  LocalAtm as LocalAtmIcon,
  PointOfSale as PointOfSaleIcon,
  Restore as RestoreIcon,
  ListAlt as ListAltIcon,
  Refresh,
  Forward,
  EventAvailableOutlined as EventNoteIcon,
  ExpandMore,
  ExpandLess,
  MailOutline,
} from "@mui/icons-material";
import { setAlert } from "../features/slices/alertSlice";
import UserProfile from "../components/wallet/UserProfile";
import Deposit from "../components/wallet/Deposit";
import Deposit2 from '../components/wallet/Deposit2';
import Deposit3 from '../components/wallet/Deposit3';

import Deposit_MCN11 from "../components/wallet/Deposit_MCN11";
import Withdrawal from "../components/wallet/Withdrawal";
import Transaction from "../components/wallet/Transaction";
import History from "../components/wallet/History";
import Backdrop from "../components/common/Backdrop";
import BetRecord from "../components/wallet/BetRecord";
import BankAccount from "../components/wallet/BankAccount";
import MissionDiary from "../components/mission/MissionDiary";

import {
  useGetProfileQuery,
  useGetShareLinkQuery,
  usePlayerBanksMutation,
  useGetBankAccountsQuery,
  useLazyPlayerGetOtpQuery,
  useLazyPlayerVerifyOtpQuery,
  useVerifyIdentityMutation,
  useGetRankInfoQuery
} from "../features/api/userApiSlice";
import {
  useGetDepositDetailsQuery,
  useDepositMutation,
  useGetWithdrawDetailsQuery,
  useWithdrawMutation,
  useGetHistoryMutation,
} from "../features/api/transactionApiSlice";
import { apiSlice } from "../features/api/apiSlice";
import { useGetNextVipQuery, useManualUpgradeVipMutation } from "../features/api/userApiSlice";
import { useGetVipRankDetailsQuery } from "../features/api/publicApiSlice";
import { findRankByNumber, findEligibleUpgrade, getVipRankLabel } from "../utils/vipRank";
import i18n, { t } from "i18next";
import Swal from "sweetalert2";
import { useGetPromotionRecordMutation } from "../features/api/bonusApiSlice";
import { useWithdrawPlayerBalanceFromLastPlatformMutation } from "../features/api/gameApiSlice";
import PromotionRecord from "../components/wallet/PromotionRecord";
import ChangePassword from "../components/wallet/ChangePassword";
import Image from "../components/common/Image";
import * as Constant from "../features/constant";
import IdVerification from "../components/wallet/IdVerification";
import SecurityCenter from "../components/wallet/SecurityCenter";
import InviteFriends from "../components/wallet/InviteFriends";
import PromoCenter from "./PromoCenter";
import RedemptionCode from "./RedemptionCode";
import ProfileList from "../components/common/ProfileList";
import OngoingBonus from "../components/wallet/OngoingBonus";
import { lighten, darken } from "@material-ui/core";
import MessagingPage from "../components/messaging/MessagingPage";
import useNotificationCount from "../hooks/useNotificationCount";

const PATH = process.env.PUBLIC_URL;

const MESSAGING_CATEGORIES = [
  { code: "PROMOTION", labelKey: "messaging.promotions", fallback: "Promotions" },
  { code: "PAYMENT", labelKey: "messaging.payment", fallback: "Payment" },
  { code: "SYSTEM", labelKey: "messaging.system", fallback: "System" },
  { code: "NOTIFICATION", labelKey: "messaging.notification", fallback: "Notification" },
  { code: "REWARDS_CLUB", labelKey: "messaging.rewardsclub", fallback: "Rewards Club" },
];

const ProfileWallet = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("lg"));
  const tabletView = useMediaQuery(theme.breakpoints.down("lg"));
  const [selectedScreen, setSelectedScreen] = useState(
    location?.state?.screen || "My Account"
  );
  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === "true";
  const [seconds, setSeconds] = useState(null);
  const searchParams = new URLSearchParams(location.search);
  const paramTab = searchParams.get("tab");
  const secondaryDark = darken(process.env.REACT_APP_COLOR_SECONDARY, 0.5);
  const [messagingExpanded, setMessagingExpanded] = useState(false);
  const [messagingCategory, setMessagingCategory] = useState("PROMOTION");
  const { counts: messagingCounts, totalUnread: messagingTotalUnread } = useNotificationCount();

  const handleIDVerificationOpen = () => {
    setIDVerificationOpen(true);
  };

  const handleIDVerificationClose = () => {
    setIDVerificationOpen(false);
  };

  const handleEmailOpen = () => {
    setEmailOpen(true);
    getOtp("EMAIL");
    setSeconds(120);
  };

  const handleEmailClose = () => {
    setEmailOpen(false);
    setEmailCode(['', '', '', '', '', '']);
  };

  const handlePhoneOpen = () => {
    setPhoneOpen(true);
    getOtp("MOBILE");
    setSeconds(120);
  };

  const handlePhoneClose = () => {
    setPhoneOpen(false);
    setPhoneCode(['', '', '', '', '', '']);
  };

  const [emailOpen, setEmailOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [emailCode, setEmailCode] = useState(['', '', '', '', '', '']);
  const [phoneCode, setPhoneCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const [IDVerificationOpen, setIDVerificationOpen] = useState(false);

  const focusNextInput = (index) => {
    if (inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const focusPreviousInput = (index) => {
    if (inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleVerifyEmail = async () => {
    // Perform email verification logic here
    let result = await verifyOtp({ channel: "EMAIL", otp: emailCode.join("") });
    if (result.isSuccess) {
      handleEmailClose();
      dispatch(
        setAlert({
          isOpen: true,
          message: t("myaccountpage.verifyotpsuccess"),
          variant: "success",
        })
      );
      window.location.reload();
    }
    else {
      dispatch(
        setAlert({
          isOpen: true,
          message: t("myaccountpage.verifyotpfailed"),
          variant: "error",
        })
      );
    }
  };

  const handleVerifyPhone = async () => {
    // Perform phone verification logic here
    let result = await verifyOtp({ channel: "MOBILE", otp: phoneCode.join("") });
    if (result.isSuccess) {
      handlePhoneClose();
      dispatch(
        setAlert({
          isOpen: true,
          message: t("myaccountpage.verifyotpsuccess"),
          variant: "success",
        })
      );
      window.location.reload();
    }
    else {
      dispatch(
        setAlert({
          isOpen: true,
          message: t("myaccountpage.verifyotpfailed"),
          variant: "error",
        })
      );
    }
  };

  const handleChangeEmail = (index, value) => {
    if (value.length > 1) {
      value = value.charAt(0);
    }
    const newEmailCode = [...emailCode];
    newEmailCode[index] = value;
    setEmailCode(newEmailCode);

    if (value !== '') {
      focusNextInput(index);
    } else {
      focusPreviousInput(index);
    }
  };

  const handleChangePhone = (index, value) => {
    if (value.length > 1) {
      value = value.charAt(0);
    }
    const newPhoneCode = [...phoneCode];
    newPhoneCode[index] = value;
    setPhoneCode(newPhoneCode);

    if (value !== '') {
      focusNextInput(index);
    } else {
      focusPreviousInput(index);
    }
  };


  const handleKeyDownPhone = (index, event) => {
    if (event.key === 'Backspace' && phoneCode[index] === '') {
      event.preventDefault();
      focusPreviousInput(index);
    }
  };
  const handleKeyDownEmail = (index, event) => {
    if (event.key === 'Backspace' && emailCode[index] === '') {
      event.preventDefault();
      focusPreviousInput(index);
    }
  };


  useEffect(() => {
    if (paramTab) {
      navigate(Constant.getPath("/profileWallet"), {
        state: {
          screen: paramTab,
        },
      });
    }
  }, [paramTab]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (seconds > 0) {
        setSeconds(prevSeconds => prevSeconds - 1);
      }
    }, 1000);

    // Cleanup function to clear the interval when component unmounts or when seconds reach 0
    return () => clearInterval(timer);
  }, [seconds]);

  const screens = [
    // {
    //   label: i18n.t("walletcard.deposit"),
    //   code: "Deposit",
    //   icon: (
    //     <div style={{ backgroundColor: "#000000", padding: "5px", borderRadius: "8px", border: "1px solid #FFFFFF", }}>
    //       <Image
    //         width="30px"
    //         height="30px"
    //         src={PATH + `/assets/img/wallet/wallet_card/Deposit.png`}
    //       />
    //     </div>
    //   ),
    //   category: t("walletcard.banking"),
    // },
    {
      label: i18n.t("walletcard.deposit"),
      code: "Deposit",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Deposit.png`}
          style={{ filter: "grayscale(1) opacity(0.6)" }} // Makes them look grey like the mockup
        />
      ),
      category: t("walletcard.banking"),
    },
    {
      label: i18n.t("walletcard.promocenter"),
      code: "Promo Center",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/promocenter.svg`}
          style={{ filter: "grayscale(1) opacity(0.6)" }}
        />
      ),
      category: t("walletcard.banking"),
    },
    ...(process.env.REACT_APP_MISSION_ENABLED === "true"
      ? [
        {
          label: i18n.t("mission.missiondiary"),
          code: "Mission Diary",
          icon: (
            <div style={{ display: "flex", justifyContent: "center", paddingLeft: 2, width: "30px", height: "30px" }}>
              <EventNoteIcon sx={{ color: "#8f7e7f", fontSize: 30, }} />
            </div>
          ),
          category: t("walletcard.banking"),
        },
      ]
      : []),
    {
      label: i18n.t("walletcard.redemptioncode"),
      code: "Redemption Code",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/redeem2.png`}
        />
      ),
      category: t("walletcard.banking"),
    },
    {
      label: i18n.t("walletcard.withdrawal"),
      code: "Withdrawal",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Withdraw.png`}
        />
      ),
      category: t("walletcard.banking"),
    },
    {
      label: i18n.t("walletcard.ongoingbonus"),
      code: "OngoingBonus",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Bet Record.png`}
        />
      ),
      category: t("walletcard.banking"),
    },
    // {
    //   label: i18n.t("walletcard.transaction"),
    //   code: "Transaction",
    //   icon: (
    //       <Image
    //         width="30px"
    //         height="30px"
    //         src={PATH + `/assets/img/wallet/wallet_card/Transaction.png`}
    //       />
    //   ),
    //   category: t("walletcard.banking"),
    // },
    {
      label: i18n.t("walletcard.history"),
      code: "History",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Transaction.png`}
        />
      ),
      category: t("walletcard.banking"),
    },
    // {
    //   label: i18n.t("walletcard.betrecord"),
    //   code: "Bet Record",
    //   icon: (
    //       <Image
    //         width="30px"
    //         height="30px"
    //         src={PATH + `/assets/img/wallet/wallet_card/Bet Record.png`}
    //       />
    //   ),
    //   category: t("walletcard.banking"),
    // },
    // {
    //   label: i18n.t("walletcard.promotionrecord"),
    //   code: "Promotion Record",
    //   icon: (
    //       <Image
    //         width="30px"
    //         height="30px"
    //         src={PATH + `/assets/img/wallet/wallet_card/Promotion.png`}
    //       />
    //   ),
    //   category: t("walletcard.banking"),
    // },
    {
      label: i18n.t("walletcard.myaccount"),
      code: "My Account",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/My Account.png`}
        />
      ),
      category: t("walletcard.account"),
    },
    {
      label: i18n.t("walletcard.bankAccount"),
      code: "Bank Account",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Bank Account.png`}
        />
      ),
      category: t("walletcard.account"),
    },
    {
      label: i18n.t("walletcard.changepassword"),
      code: "Change Password",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Change Password.png`}
        />
      ),
      category: t("walletcard.account"),
    },
    {
      label: i18n.t("messaging.title", "Messaging"),
      code: "Messaging",
      icon: (
        <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px" }}>
          <MailOutline sx={{ color: "#8f7e7f", fontSize: 26 }} />
          {messagingTotalUnread > 0 && (
            <Box component="span" sx={{
              position: "absolute", top: -4, right: -4,
              backgroundColor: "#d32f2f", color: "white",
              fontSize: "9px", fontWeight: 600, lineHeight: 1,
              height: 16, minWidth: 16, borderRadius: "8px",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              padding: "0 3px",
            }}>
              {messagingTotalUnread > 99 ? "99+" : messagingTotalUnread}
            </Box>
          )}
        </Box>
      ),
      category: t("walletcard.account"),
    },

  ];


  const screensMobile = [
    {
      label: i18n.t("walletcard.promocenter"),
      code: "Promo Center",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/promocenter.svg`}
        />
      ),
      category: t("walletcard.banking"),
      onClick: () => {
        navigate(Constant.getPath("/promocenter"), {
          state: {
            isNotProfileWallet: true,
            user: user?.result,
          },
        })
      }
    },
    ...(process.env.REACT_APP_MISSION_ENABLED === "true"
      ? [
        {
          label: i18n.t("mission.missiondiary"),
          code: "Mission Diary",
          icon: (
            <div style={{ display: "flex", justifyContent: "center", paddingLeft: 2, width: "30px", height: "30px" }}>
              <EventNoteIcon sx={{ color: "#9e9e9f", fontSize: 30, }} />
            </div>
          ),
          category: t("walletcard.banking"),
          onClick: () => {
            navigate(Constant.getPath("/mission"), {
              state: {
                userId: user?.result?.userId
              }
            })
          }
        }
      ]
      : []),
    {
      label: i18n.t("walletcard.redemptioncode"),
      code: "Redemption Code",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/redeem2.png`}
          // redeem2.png is pure white on transparent, which is correct on the dark desktop
          // sidebar but invisible on this light mobile list. brightness(0) flattens it to
          // black, invert(0.62) lifts that to #9E9E9E - matching the grey of the
          // neighbouring icons. Must go through sx: Image overwrites the `style` prop.
          sx={{ filter: "brightness(0) invert(0.62)" }}
        />
      ),
      category: t("walletcard.banking"),
      onClick: () => {
        navigate(Constant.getPath("/redemptioncode"), {
          state: {
            isNotProfileWallet: true,
            user: user?.result,
          },
        })
      }
    },
    {
      label: i18n.t("walletcard.ongoingbonus"),
      code: "Bet Record",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Bet Record.png`}
        />
      ),
      category: t("walletcard.banking"),
      onClick: () => {
        navigate(Constant.getPath("/ongoingbonus"),
          {
            state: {
              withdrawDetails: withdrawDetails?.result
            }
          })
      }
    },
    // {
    //   label: i18n.t("walletcard.transaction"),
    //   code: "Transaction",
    //   icon: (
    //     <Image
    //       width="30px"
    //       height="30px"
    //       src={PATH + `/assets/img/wallet/wallet_card/Transaction.png`}
    //     />
    //   ),
    //   category: t("walletcard.banking"),
    //   onClick: () => {
    //     navigate(Constant.getPath("/transaction"), {
    //       state: {
    //         userId: user?.result?.userId
    //       }
    //     })
    //   }
    // },
    {
      label: i18n.t("walletcard.history"),
      code: "History",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Transaction.png`}
        />
      ),
      category: t("walletcard.banking"),
      onClick: () => {
        navigate(Constant.getPath("/history"))
      }
    },
    // {
    //   label: i18n.t("walletcard.betrecord"),
    //   code: "Bet Record",
    //   icon: (
    //     <Image
    //       width="30px"
    //       height="30px"
    //       src={PATH + `/assets/img/wallet/wallet_card/Bet Record.png`}
    //     />
    //   ),
    //   category: t("walletcard.banking"),
    //   onClick: () => {
    //     navigate(Constant.getPath("/betrecord"))
    //   }
    // },
    // {
    //   label: i18n.t("walletcard.promotionrecord"),
    //   code: "Promotion Record",
    //   icon: (
    //     <Image
    //       width="30px"
    //       height="30px"
    //       src={PATH + `/assets/img/wallet/wallet_card/Promotion.png`}
    //     />
    //   ),
    //   category: t("walletcard.banking"),
    //   onClick: () => {
    //     navigate(Constant.getPath("/promorecord"))
    //   }
    // },
    {
      label: i18n.t("walletcard.myaccount"),
      code: "My Account",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/My Account.png`}
        />
      ),
      category: t("walletcard.account"),
      onClick: () => {
        navigate(Constant.getPath("/myaccount"), {
          state: {
            user: user?.result,
            shareLink: shareLink?.result?.url
          }
        })
      }
    },
    {
      label: i18n.t("walletcard.bankAccount"),
      code: "Bank Account",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Bank Account.png`}
        />
      ),
      category: t("walletcard.account"),
      onClick: () => {
        navigate(Constant.getPath("/bankaccount"))
      }
    },
    {
      label: i18n.t("walletcard.changepassword"),
      code: "Change Password",
      icon: (
        <Image
          width="30px"
          height="30px"
          src={PATH + `/assets/img/wallet/wallet_card/Change Password.png`}
        />
      ),
      category: t("walletcard.account"),
      onClick: () => {
        navigate(Constant.getPath("/changepassword"))
      }
    },
    {
      label: i18n.t("messaging.title", "Messaging"),
      code: "Messaging",
      icon: (
        <Box sx={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: "30px", height: "30px" }}>
          <MailOutline sx={{ color: "#9e9e9f", fontSize: 26 }} />
          {messagingTotalUnread > 0 && (
            <Box component="span" sx={{
              position: "absolute", top: -4, right: -4,
              backgroundColor: "#d32f2f", color: "white",
              fontSize: "9px", fontWeight: 600, lineHeight: 1,
              height: 16, minWidth: 16, borderRadius: "8px",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              padding: "0 3px",
            }}>
              {messagingTotalUnread > 99 ? "99+" : messagingTotalUnread}
            </Box>
          )}
        </Box>
      ),
      category: t("walletcard.account"),
      onClick: () => {
        navigate(Constant.getPath("/messaging"))
      }
    },

  ];

  const {
    data: rankInfo,
    isFetching: isrankInfoFetching,
    isLoading: isrankInfoLoading,
    isError: isrankInfoError,
    isSuccess: isrankInfoSuccess,
    error: rankInfoError,
  } = useGetRankInfoQuery();

  const {
    data: nextVip,
    isFetching: isNextVipFetching,
    isLoading: isNextVipLoading,
    isError: isNextVipError,
    isSuccess: isNextVipSuccess,
    error: NextVipError,
  } = useGetNextVipQuery();

  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
    isSuccess: isUserSuccess,
    error: userError,
  } = useGetProfileQuery();

  // Resolve operator-defined display name for this player's rank. RTK Query
  // dedupes with the /vip page hook — no additional network request.
  const { data: vipRankDetails } = useGetVipRankDetailsQuery();
  const rankNumber = parseInt(String(user?.result?.playerRank || "").replace(/\D/g, ""), 10);
  const rankRecord = findRankByNumber(vipRankDetails?.result?.vipRanks, rankNumber);
  const customRankName = rankRecord?.customDisplayName?.trim();

  // Manual VIP upgrade: eligible target (Option A cascade) when a manual gate
  // exists above the player's current rank and they're eligible for it.
  // Prefer `accumulatedDeposit` (what Vip2 also reads) with `totalDepositedAmount`
  // as a fallback — different tenants expose one or the other on /profile.
  const eligibleUpgrade = findEligibleUpgrade(
    user?.result?.playerRank,
    user?.result?.accumulatedDeposit ?? user?.result?.totalDepositedAmount,
    vipRankDetails?.result?.vipRanks,
  );
  const [triggerManualUpgrade, { isLoading: isManualUpgradingVip }] = useManualUpgradeVipMutation();
  const handleManualUpgradeVip = async () => {
    try {
      const res = await triggerManualUpgrade().unwrap();
      if (res?.success) {
        const currency = process.env.REACT_APP_CURRENCY_CODE || "USD";
        const newRankLabel = getVipRankLabel({
          vipRank: parseInt(String(res?.result?.newRank || "").replace(/\D/g, ""), 10),
          customDisplayName: eligibleUpgrade?.customDisplayName,
        });
        Swal.fire({
          icon: "success",
          title: `Upgraded to ${newRankLabel}`,
          text: `${res?.result?.upgradeAmount || 0} ${currency} bonus credited`,
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", title: "Upgrade failed", text: res?.message || "Please try again" });
      }
    } catch (e) {
      Swal.fire({ icon: "error", title: "Upgrade failed", text: e?.data?.message || e?.message || "Please try again" });
    }
  };

  const {
    data: shareLink,
    isLoading: isShareLinkLoading,
    isError: isShareLinkError,
    isSuccess: isShareLinkSuccess,
    error: shareLinkError,
  } = useGetShareLinkQuery();

  const {
    data: depositDetails,
    isLoading: isDepositDetailsLoading,
    isError: isDepositDetailsError,
    isSuccess: isDepositDetailsSuccess,
    error: depositDetailsError,
  } = useGetDepositDetailsQuery();

  const {
    data: withdrawDetails,
    isLoading: isWithdrawDetailsLoading,
    isError: isWithdrawDetailsError,
    isSuccess: isWithdrawDetailsSuccess,
    error: withdrawDetailsError,
  } = useGetWithdrawDetailsQuery();

  const [withdrawLastPlatform, { isWithdrawLastPlatformLoading }] = useWithdrawPlayerBalanceFromLastPlatformMutation();
  const [isWithdrawLastPlatformProcessing, setIsWithdrawLastPlatformProcessing] = useState(false);
  const refreshCooldownUntilRef = useRef(0);

  const [
    getOtp,
    {
      data: getOtpData,
      isLoading: isGetOtpLoading,
      isError: isGetOtpError,
      isSuccess: isGetOtpSuccess,
      error: getOtpError,
    },
  ] = useLazyPlayerGetOtpQuery({ prefetch: true });

  const [
    verifyOtp,
    {
      data: verifyOtpData,
      isLoading: isVerifyOtpLoading,
      isError: isVerifyOtpError,
      isSuccess: isVerifyOtpSuccess,
      error: verifyOtpError,
    },
  ] = useLazyPlayerVerifyOtpQuery({ prefetch: true });

  useEffect(() => {
    const screen = location?.state?.screen || "My Account";
    setSelectedScreen(screen);
    if (screen === "Messaging") {
      setMessagingExpanded(true);
    }
  }, [location]);

  if (
    isUserLoading ||
    isShareLinkLoading ||
    isDepositDetailsLoading ||
    isWithdrawDetailsLoading
  ) {
    return <Backdrop />;
  }

  const formatNumber = (num) =>
    num >= 1e6 ? `${(num / 1e6).toFixed(2)}M` : num;

  return (
    <>
      <Box
        px={2}
        py={3}
        sx={{
          backgroundColor: theme.palette.background.secondaryPaper,
          minHeight: mobileView && "100vh",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {!mobileView && (
          <Container>
            <Grid container direction="row" spacing={3}>
              {/* Desktop Sidebar */}
              <Grid item xs={3}>
                <Box
                  display="flex"
                  justifyContent="flex-start"
                  flexDirection="column"
                  sx={{
                    bgcolor: process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY || theme.palette.background.paper,
                    height: "100%",
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.05)",
                    borderRadius: 3
                  }}
                >

                  {/* User Header Section */}
                  <Box
                    mt={2}
                    mx={2}
                    mb={0.5}
                    p={1}
                    sx={{
                      borderRadius: '12px',
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    {/* Profile Icon / Avatar area */}
                    <Box position="relative" display="flex" alignItems="center">
                      <AccountCircleIcon sx={{ fontSize: 60, color: theme.palette.grey[400] }} />
                    </Box>

                    <Stack spacing={0.5} ml={2} flex={1}>
                      {/* Username */}
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2, color: process.env.REACT_APP_COLOR_TEXT }}>
                        {user?.result?.username}
                      </Typography>

                      {/* Rank Badge */}
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: theme.palette.secondary.main,
                            borderRadius: '20px',
                            pr: 1.5,
                            pl: 0.5,
                            py: 0.2
                          }}
                        >
                          <Box
                            component="img"
                            src={rankInfo?.result?.privileges[user?.result?.playerRank]?.logo}
                            sx={{ width: 20, height: 20, mr: 0.5, ml: 0.5 }}
                          />
                          <Typography variant="caption" fontWeight="bold" color="white">
                            {customRankName || user?.result?.playerRank}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Balance & Refresh*/}
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2" fontWeight="bold" color={process.env.REACT_APP_COLOR_PRIMARY}>
                          {process.env.REACT_APP_CURRENCY_CODE}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" color={process.env.REACT_APP_COLOR_TEXT}>
                          {user?.result?.currentCredit?.toLocaleString()}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (Date.now() < refreshCooldownUntilRef.current) {
                              Swal.fire({
                                title: t("header.refreshingtitle"),
                                text: t("header.refreshingbalance"),
                                icon: "info",
                              });
                              return;
                            }
                            refreshCooldownUntilRef.current = Date.now() + 5000;
                            dispatch(apiSlice.util.invalidateTags(["Profile"]));
                          }}
                          sx={{ p: 0.2 }}
                        >
                          <Refresh sx={{ color: "#29A829", fontSize: 16 }} />
                        </IconButton>
                      </Box>

                      {/* Points display */}
                      {process.env.REACT_APP_MISSION_ENABLED && (
                        <Typography variant="caption" sx={{ color: process.env.REACT_APP_COLOR_TEXT, display: 'flex', alignItems: 'center' }}>
                          {user?.result?.activityPoint || 0} Points
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection={"column"}
                    alignSelf="center"
                    width={"100%"}
                    my={{ xs: 5, sm: 0, md: 0, lg: 0 }}
                  >

                    <Box
                      display="flex"
                      flexDirection="column"
                      sx={{
                        px: 2,
                        mx: 2,
                        mt: .5,
                        py: 1.5,
                        borderRadius: 2,
                        background: `
                          repeating-linear-gradient(
                            45deg,
                            rgba(255, 255, 255, 0.08),
                            rgba(255, 255, 255, 0.08) 2px,
                            transparent 2px,
                            transparent 10px
                          ),                  
                          linear-gradient(135deg, #b8860b 0%, #ffcc33 100%)
                                    `,
                      }}
                    >
                      <Typography fontWeight={"bold"} fontSize={"x-large"} color={"#3E2723"}>
                        {t('walletcard.viprank')}
                      </Typography>

                      <Typography fontSize={"x-small"} color={"#3E2723"}>
                        {(() => {
                          const depositVipAmount = Math.max(0, Number(nextVip?.result?.nextDepositAccumulate) - Number(nextVip?.result?.depositAmount));
                          const depositVipText = t("walletcard.depositvip", { amount: depositVipAmount });
                          const depositVipAmountStr = String(depositVipAmount);
                          const idx = depositVipText.indexOf(depositVipAmountStr);
                          if (idx === -1) return depositVipText;
                          return (
                            <>
                              {depositVipText.slice(0, idx)}
                              <Box component="span" sx={{ color: process.env.REACT_APP_COLOR_PRIMARY || theme.palette.primary.main, fontWeight: "bold" }}>
                                {`${depositVipAmount.toLocaleString()} ${process.env.REACT_APP_CURRENCY_CODE} `}
                              </Box>
                              {depositVipText.slice(idx + depositVipAmountStr.length)}
                            </>
                          );
                        })()}
                      </Typography>

                      {eligibleUpgrade && (
                        <Box mt={1}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleManualUpgradeVip}
                            disabled={isManualUpgradingVip}
                            sx={{
                              backgroundColor: '#4ade80',
                              color: '#000',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              textTransform: 'none',
                              px: 1.5,
                              py: 0.25,
                              minWidth: 'auto',
                              borderRadius: '6px',
                              '&:hover': { backgroundColor: '#22c55e' },
                            }}
                          >
                            {t("vipnew.upgradenow", {
                              rank: getVipRankLabel(eligibleUpgrade),
                              defaultValue: `Upgrade to ${getVipRankLabel(eligibleUpgrade)}`,
                            })}
                          </Button>
                        </Box>
                      )}

                      <Box mt={1} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}
                      >
                        <Typography fontWeight={"bold"} fontSize={"small"} color={"#3E2723"}>
                          {customRankName || user?.result?.playerRank}
                        </Typography>
                        <Typography fontWeight={"bold"} fontSize={"small"} color={"#3E2723"}>
                          {nextVip?.result?.nextPlayerRank}
                        </Typography>
                      </Box>


                      <LinearProgress
                        variant="buffer"
                        value={Math.min(
                          ((nextVip?.result?.depositAmount || 0) /
                            (nextVip?.result?.nextDepositAccumulate || 1)) * 100,
                          100
                        )}
                        valueBuffer={100}
                        color="primary"
                        sx={{
                          my: 1,
                          width: "100%",
                          borderRadius: 5,
                          height: 5,
                          '& .MuiLinearProgress-bar2Buffer': {
                            backgroundColor: '#888',
                          },
                        }}
                      />

                      <Box display={"flex"} flexDirection={"row"} justifyContent={"center"}>
                        <Typography fontWeight={"bold"} fontSize={"x-small"} color={"#3E2723"}>
                          {`${Math.min(
                            Math.round(
                              ((nextVip?.result?.depositAmount || 0) /
                                (nextVip?.result?.nextDepositAccumulate || 1)) * 100
                            ),
                            100
                          )}%`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box >

                  <Box mb={3}>
                    {screens?.map((item, idx, arr) => {
                      const isSelected = selectedScreen === item.code;
                      const showCategory = idx === 0 || item?.category !== arr[idx - 1]?.category;
                      const isMessaging = item.code?.toLowerCase() === "messaging";

                      return (
                        <React.Fragment key={idx}>
                          {showCategory && (
                            <Box px={3} pt={3} pb={1}>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: "bold", color: process.env.REACT_APP_COLOR_TEXT, fontSize: "1.2rem" }}
                              >
                                {item.category}
                              </Typography>
                            </Box>
                          )}

                          <Box px={2} mb={0.5}> {/* Added margin bottom for separation */}
                            <ListItemButton
                              onClick={() => {
                                if (isMessaging) {
                                  if (!messagingExpanded) {
                                    setMessagingExpanded(true);
                                    navigate(Constant.getPath("/profileWallet"), {
                                      replace: true,
                                      state: { screen: "Messaging" },
                                    });
                                  } else {
                                    setMessagingExpanded(false);
                                  }
                                } else {
                                  setMessagingExpanded(false);
                                  navigate(Constant.getPath("/profileWallet"), {
                                    replace: true,
                                    state: { screen: item.code },
                                  });
                                }
                              }}
                              sx={{
                                borderRadius: "4px",
                                py: 1.5,
                                backgroundColor: isSelected ? "transparent" : process.env.REACT_APP_COLOR_BACKGROUND_SECONDARY,
                                background: isSelected
                                  ? `linear-gradient(
                              to left,
                              ${process.env.REACT_APP_COLOR_SECONDARY} 0%,
                              white 800%
                            )`
                                  : secondaryDark,
                                transition: "0.3s",
                                "&:hover": {
                                  backgroundColor: isSelected ? "transparent" : secondaryDark,
                                },
                              }}
                            >
                              <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                  {/* Active icon styling */}
                                  <Box sx={{ filter: isSelected ? "brightness(0) saturate(100%)" : "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {item.icon}
                                  </Box>
                                  <Typography
                                    sx={{
                                      fontWeight: isSelected ? "bold" : "normal",
                                      color: 'white'
                                    }}
                                  >
                                    {item.label}
                                  </Typography>
                                </Stack>
                                {isMessaging && (
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    {messagingExpanded ? (
                                      <ExpandLess sx={{ color: "white" }} />
                                    ) : (
                                      <ExpandMore sx={{ color: "white" }} />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </ListItemButton>

                            {isMessaging && (
                              <Collapse in={messagingExpanded} timeout="auto" unmountOnExit>
                                {MESSAGING_CATEGORIES.map((cat) => {
                                  const catSelected = selectedScreen === "Messaging" && messagingCategory === cat.code;
                                  return (
                                    <ListItemButton
                                      key={cat.code}
                                      onClick={() => {
                                        setMessagingCategory(cat.code);
                                        navigate(Constant.getPath("/profileWallet"), {
                                          replace: true,
                                          state: { screen: "Messaging" },
                                        });
                                      }}
                                      sx={{
                                        pl: 5,
                                        py: 0.8,
                                        mt: 0.5,
                                        borderRadius: "4px",
                                        backgroundColor: catSelected ? process.env.REACT_APP_COLOR_SECONDARY : secondaryDark,
                                        transition: "0.3s",
                                        "&:hover": {
                                          backgroundColor: catSelected
                                            ? process.env.REACT_APP_COLOR_SECONDARY
                                            : darken(process.env.REACT_APP_COLOR_SECONDARY, 0.35),
                                        },
                                      }}
                                    >
                                      <Typography fontSize={13} sx={{ flex: 1, color: 'white', fontWeight: catSelected ? "bold" : "normal" }}>
                                        {t(cat.labelKey, cat.fallback)}
                                      </Typography>
                                      {(messagingCounts?.[cat.code] || 0) > 0 && (
                                        <Box component="span" sx={{
                                          backgroundColor: "#d32f2f", color: "white", fontSize: "10px", fontWeight: 600, lineHeight: 1,
                                          height: 18, minWidth: 18, borderRadius: "9px", display: "inline-flex", alignItems: "center",
                                          justifyContent: "center", padding: "0 4px",
                                        }}>
                                          {messagingCounts[cat.code]}
                                        </Box>
                                      )}
                                    </ListItemButton>
                                  );
                                })}
                              </Collapse>
                            )}
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Box>

                  {/* User Verification Section */}
                  {process.env.REACT_APP_SHOW_VERIFY_ACCOUNT === 'true' ? (
                    <Box px={2} py={2}>
                      <Typography variant="subtitle2" sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white", fontWeight: "bold" }}>
                        {t("Verify Your Account NOW")}
                      </Typography>
                      <Box
                        sx={{
                          marginTop: 1,
                          backgroundColor: secondaryDark,
                          padding: '5px',
                          borderRadius: 1,
                        }}>
                        <Box sx={{ display: "flex", paddingTop: "5px" }}>
                          <Grid container spacing={2} justifyContent="center">
                            <Grid item sx={{ paddingBottom: '5px' }}>
                              <IconButton
                                style={{ width: '35px', height: '35px', marginLeft: '10px' }}>
                                {user?.result?.isPhoneVerified ? (
                                  <img
                                    src={PATH + "/assets/img/wallet/verified_icon/mobile_verified.png"}
                                    style={{ height: '35px' }}
                                  />
                                ) : (
                                  <img
                                    src={PATH + "/assets/img/wallet/verified_icon/mobile_pre_verified.png"}
                                    style={{ height: '35px' }}
                                    onClick={handlePhoneOpen}
                                  />
                                )}
                                <Dialog open={phoneOpen} onClose={handlePhoneClose} BackdropProps={{ invisible: true }}>
                                  <DialogTitle sx={{ padding: 0 }}>
                                    <DialogActions>
                                      <Button onClick={handlePhoneClose}>X</Button>
                                    </DialogActions>
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '20px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white',
                                      }}
                                    >
                                      Verify Mobile Number</Typography>
                                  </DialogTitle>
                                  <DialogContent>
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white'
                                      }}
                                    >
                                      A 6-digit PIN number has been sent to your phone.</Typography>

                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white',
                                        paddingTop: '5px'
                                      }}
                                    >
                                      ********{user?.result?.mobileNumber?.substring(user?.result?.mobileNumber?.length - 4, user?.result?.mobileNumber?.length - 1)}</Typography>
                                    <br />
                                    <Grid container spacing={1} alignItems="center">
                                      {[...Array(6)].map((_, index) => (
                                        <Grid item key={index}>
                                          <TextField
                                            variant="outlined"
                                            size="small"
                                            inputRef={(el) => (inputRefs.current[index] = el)}
                                            value={phoneCode[index]}
                                            onChange={(e) => handleChangePhone(index, e.target.value)}
                                            inputProps={{
                                              maxLength: 1,
                                              style: { textAlign: 'center', width: '10px', height: '15px' }
                                            }}
                                          />
                                        </Grid>
                                      ))}
                                    </Grid>
                                    <br />
                                    <DialogActions sx={{ justifyContent: 'center' }}>
                                      <Button variant="contained" color="error" onClick={handleVerifyPhone}>
                                        VERIFY
                                      </Button>
                                    </DialogActions>
                                    <br />
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white'
                                      }}>
                                      Experiencing any issues?{' '}
                                      <Link href='/contactus' style={{ color: 'red', textDecoration: 'none' }}>
                                        Contact Customer Support
                                      </Link>
                                    </Typography>
                                    <br />
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white'
                                      }}
                                    >
                                      Didn't receive the PIN number?{' '}
                                      <Typography
                                        component="span"
                                        sx={{ color: 'green', fontSize: '12px', textDecoration: 'none', cursor: seconds === 0 ? 'pointer' : 'not-allowed' }}
                                        onClick={seconds === 0 ? handlePhoneOpen : null}
                                      >
                                        Resend code ({seconds}s)
                                      </Typography>
                                    </Typography>


                                  </DialogContent>
                                </Dialog>
                              </IconButton>
                            </Grid>
                            <Grid item sx={{ paddingBottom: '5px' }}>
                              <IconButton
                                style={{ height: '35px', marginLeft: '10px' }}>
                                {user?.result?.isEmailVerified ? (
                                  <img
                                    src={PATH + "/assets/img/wallet/verified_icon/email_verified.png"}
                                    style={{ height: '35px' }}
                                    alt="Email Verified"
                                  />
                                ) : (
                                  <img
                                    src={PATH + "/assets/img/wallet/verified_icon/email_pre_verified.png"}
                                    style={{ height: '35px' }}
                                    onClick={handleEmailOpen}
                                    alt="Email Not Verified"

                                  />
                                )}
                                <Dialog open={emailOpen} onClose={handleEmailOpen} BackdropProps={{ invisible: true }}>
                                  <DialogTitle sx={{ padding: 0 }}>
                                    <DialogActions>
                                      <Button onClick={handleEmailClose}>X</Button>
                                    </DialogActions>
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '20px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white',
                                      }}
                                    >
                                      Verify Email</Typography>
                                  </DialogTitle>
                                  <DialogContent>
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white'
                                      }}
                                    >
                                      A 6-digit PIN number has been sent to your email.</Typography>

                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white',
                                        paddingTop: '5px'
                                      }}
                                    >
                                      {user?.result?.email?.substring(0, 2)}******@{user?.result?.email?.split("@")[1]}
                                    </Typography>
                                    <br />
                                    <Grid container spacing={1} alignItems="center">
                                      {[...Array(6)].map((_, index) => (
                                        <Grid item key={index}>
                                          <TextField
                                            variant="outlined"
                                            size="small"
                                            inputRef={(el) => (inputRefs.current[index] = el)}
                                            value={emailCode[index]}
                                            onChange={(e) => handleChangeEmail(index, e.target.value)}
                                            inputProps={{
                                              maxLength: 1,
                                              style: { textAlign: 'center', width: '10px', height: '15px' }
                                            }}
                                          />
                                        </Grid>
                                      ))}
                                    </Grid>
                                    <br />
                                    <DialogActions sx={{ justifyContent: 'center' }}>
                                      <Button variant="contained" color="error" onClick={handleVerifyEmail}>
                                        VERIFY
                                      </Button>
                                    </DialogActions>
                                    <br />
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white'
                                      }}>
                                      Experiencing any issues?{' '}
                                      <Link href='/contactus' style={{ color: 'red', textDecoration: 'none' }}>
                                        Contact Customer Support
                                      </Link>
                                    </Typography>
                                    <br />
                                    <Typography
                                      sx={{
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        color: process.env.REACT_APP_COLOR_TEXT || 'white'
                                      }}
                                    >
                                      Didn't receive the PIN number?{' '}
                                      <Typography
                                        component="span"
                                        sx={{ color: 'green', fontSize: '12px', textDecoration: 'none', cursor: seconds === 0 ? 'pointer' : 'not-allowed' }}
                                        onClick={seconds === 0 ? handleEmailOpen() : null}
                                      >
                                        Resend code ({seconds}s)
                                      </Typography>
                                    </Typography>
                                  </DialogContent>
                                </Dialog>
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                      </Box>
                    </Box>
                  ) : []}
                </Box>
              </Grid>

              <Grid item xs direction="column" spacing={3}>
                <Grid item xs>
                  <Box
                    display="flex"
                    justifyContent="flex-start"
                    flexDirection="column"
                    px={3}
                    py={2}
                    sx={{
                      bgcolor: process.env.REACT_APP_COLOR_PROFILE_BACKGROUND || theme.palette.background.paper,
                      height: "100%",
                      borderRadius: 3
                    }}
                  >
                    {selectedScreen?.toLowerCase() === "my account" && (
                      <UserProfile
                        user={user?.result}
                        shareLink={shareLink?.result?.url}
                      />
                    )}
                    {selectedScreen?.toLowerCase() === "deposit" && (
                      process.env.REACT_APP_DEPOSIT_VERSION === 'Deposit' ? (
                        <Deposit
                          depositDetails={depositDetails?.result}
                          useDepositMutation={useDepositMutation}
                        />
                      ) : process.env.REACT_APP_DEPOSIT_VERSION === 'Deposit3' ? (
                        <Deposit3
                          depositDetails={depositDetails?.result}
                          useDepositMutation={useDepositMutation}
                        />
                      ) : process.env.REACT_APP_DEPOSIT_VERSION === 'Deposit_MCN11' ? (
                        <Deposit_MCN11
                          depositDetails={depositDetails?.result}
                          useDepositMutation={useDepositMutation}
                        />
                      ) : (
                        <Deposit2
                          depositDetails={depositDetails?.result}
                          useDepositMutation={useDepositMutation}
                        />
                      )
                    )}
                    {selectedScreen?.toLowerCase() === "withdrawal" && (
                      <Withdrawal
                        withdrawDetails={withdrawDetails?.result}
                        useWithdrawMutation={useWithdrawMutation}
                        user={user?.result}
                        useGetHistoryMutation={useGetHistoryMutation}
                        useGetBankAccountsQuery={useGetBankAccountsQuery}
                        useGetPromotionRecordMutation={useGetPromotionRecordMutation}
                        setSelectedScreen={setSelectedScreen}
                      />
                    )}
                    {selectedScreen?.toLowerCase() === "ongoingbonus" && (
                      <OngoingBonus
                        withdrawDetails={withdrawDetails?.result}
                      />
                    )}

                    {selectedScreen?.toLowerCase() === "transaction" && (
                      <Transaction />
                    )}
                    {selectedScreen?.toLowerCase() === "history" && (
                      <History />
                    )}
                    {selectedScreen?.toLowerCase() === "bet record" && (
                      <BetRecord />
                    )}
                    {selectedScreen?.toLowerCase() === "promotion record" && (
                      <PromotionRecord />
                    )}
                    {selectedScreen?.toLowerCase() === "bank account" && (
                      <BankAccount />
                    )}
                    {selectedScreen.toLowerCase() === "change password" && (
                      <ChangePassword />
                    )}
                    {selectedScreen?.toLowerCase() === "id verification" && (
                      <IdVerification
                        useVerifyIdentityMutation={useVerifyIdentityMutation}
                      />
                    )}
                    {selectedScreen?.toLowerCase() === "security center" && (
                      <SecurityCenter
                        usePlayerBanksMutation={usePlayerBanksMutation}
                      />
                    )}
                    {selectedScreen?.toLowerCase() === "invite friends" && (
                      <InviteFriends
                        usePlayerBanksMutation={usePlayerBanksMutation}
                        shareLink={shareLink?.result?.url}
                      />
                    )}

                    {selectedScreen?.toLowerCase() === "promo center" && (
                      <PromoCenter
                        user={user?.result}
                      />
                    )}

                    {selectedScreen?.toLowerCase() === "mission diary" && (
                      <MissionDiary
                        user={user?.result}
                      />
                    )}

                    {selectedScreen?.toLowerCase() === "redemption code" && (
                      <RedemptionCode
                        user={user?.result}
                      />
                    )}

                    {selectedScreen?.toLowerCase() === "messaging" && (
                      <MessagingPage
                        initialCategory={messagingCategory}
                        embedded
                        onCategoryChange={(cat) => setMessagingCategory(cat)}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Container>
        )}
        {mobileView && (
          <Box display="flex" flexDirection="column" sx={{
          }}>
            <Box
              display="flex"
              flexDirection={"column"}
              alignSelf="center"
              width={"100%"}
              my={{ xs: 5, sm: 0, md: 0, lg: 0 }}
            >
              <Box paddingX={mobileView ? .5 : 5} width={"100%"} marginTop={0.6} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
                <Box paddingX={1} display={"flex"} flexDirection={"row"} alignItems={"center"}>
                  <AccountCircleIcon sx={{ fontSize: 70, color: theme.palette.primary.main || process.env.REACT_APP_COLOR_ICON || theme.components?.MuiSvgIcon?.styleOverrides?.root?.color, }} />
                  <Box ml={1.5}>
                    <Typography fontSize={"medium"} fontWeight={"bold"} variant="body2" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
                      {user?.result?.username}
                    </Typography>

                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: theme.palette.secondary.main,
                          borderRadius: '20px',
                          pr: 1.5,
                          pl: 0.5,
                          py: 0.2
                        }}
                      >
                        <Box
                          component="img"
                          src={rankInfo?.result?.privileges[user?.result?.playerRank]?.logo}
                          sx={{ width: 20, height: 20, mr: 0.5, ml: 0.5 }}
                        />
                        <Typography variant="caption" fontWeight="bold" color="white">
                          {customRankName || user?.result?.playerRank}
                        </Typography>
                      </Box>
                    </Box>

                    {process.env.REACT_APP_MISSION_ENABLED && (
                      <Typography variant="caption" sx={{ color: process.env.REACT_APP_COLOR_TEXT, display: 'flex', alignItems: 'center' }}>
                        {user?.result?.activityPoint || 0} Points
                      </Typography>
                    )}
                  </Box>
                </Box >
              </Box >

              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  px: 2,
                  mx: 2,
                  mt: .5,
                  py: 1.5,
                  borderRadius: 2,
                  background: `
        repeating-linear-gradient(
          45deg,
          rgba(255, 255, 255, 0.08),
          rgba(255, 255, 255, 0.08) 2px,
          transparent 2px,
          transparent 10px
        ),                  
        linear-gradient(135deg, #b8860b 0%, #ffcc33 100%)
                  `,
                }}
              >
                <Typography fontWeight={"bold"} fontSize={"x-large"} color={"#3E2723"}>
                  {t('walletcard.viprank')}
                </Typography>

                <Typography fontSize={"x-small"} color={"#3E2723"}>
                  {(() => {
                    const depositVipAmount = Math.max(0, Number(nextVip?.result?.nextDepositAccumulate) - Number(nextVip?.result?.depositAmount));
                    const depositVipText = t("walletcard.depositvip", { amount: depositVipAmount });
                    const depositVipAmountStr = String(depositVipAmount);
                    const idx = depositVipText.indexOf(depositVipAmountStr);
                    if (idx === -1) return depositVipText;
                    return (
                      <>
                        {depositVipText.slice(0, idx)}
                        <Box component="span" sx={{ color: process.env.REACT_APP_COLOR_PRIMARY || theme.palette.primary.main, fontWeight: "bold" }}>
                          {` ${depositVipAmount.toLocaleString()} ${process.env.REACT_APP_CURRENCY_CODE}`}
                        </Box>
                        {depositVipText.slice(idx + depositVipAmountStr.length)}
                      </>
                    );
                  })()}
                </Typography>

                {eligibleUpgrade && (
                  <Box mt={1}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleManualUpgradeVip}
                      disabled={isManualUpgradingVip}
                      sx={{
                        backgroundColor: '#4ade80',
                        color: '#000',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        px: 1.5,
                        py: 0.25,
                        minWidth: 'auto',
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: '#22c55e' },
                      }}
                    >
                      {t("vipnew.upgradenow", {
                        rank: getVipRankLabel(eligibleUpgrade),
                        defaultValue: `Upgrade to ${getVipRankLabel(eligibleUpgrade)}`,
                      })}
                    </Button>
                  </Box>
                )}

                <Box mt={1} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}
                >
                  <Typography fontWeight={"bold"} fontSize={"small"} color={"#3E2723"}>
                    {customRankName || user?.result?.playerRank}
                  </Typography>
                  <Typography fontWeight={"bold"} fontSize={"small"} color={"#3E2723"}>
                    {nextVip?.result?.nextPlayerRank}
                  </Typography>
                </Box>


                <LinearProgress
                  variant="buffer"
                  value={Math.min(
                    ((nextVip?.result?.depositAmount || 0) /
                      (nextVip?.result?.nextDepositAccumulate || 1)) * 100,
                    100
                  )}
                  valueBuffer={100}
                  color="primary"
                  sx={{
                    my: 1,
                    width: "100%",
                    borderRadius: 5,
                    height: 5,
                    '& .MuiLinearProgress-bar2Buffer': {
                      backgroundColor: '#888',
                    },
                  }}
                />

                <Box display={"flex"} flexDirection={"row"} justifyContent={"center"}>
                  <Typography fontWeight={"bold"} fontSize={"x-small"} color={"#3E2723"}>
                    {`${Math.min(
                      Math.round(
                        ((nextVip?.result?.depositAmount || 0) /
                          (nextVip?.result?.nextDepositAccumulate || 1)) * 100
                      ),
                      100
                    )}%`}
                  </Typography>
                </Box>
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{
                  px: 2,
                  mx: 2,
                  mt: 1,
                  py: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  color: isLightTheme ? "white" : process.env.REACT_APP_COLOR_TEXT || "white"
                }}
              >
                <Box>
                  <Typography fontWeight={"bold"} fontSize={"x-small"} color={process.env.REACT_APP_COLOR_TEXT || "white"} >
                    {t("walletcard.walletbalance")}
                  </Typography>
                  <Box pl={1} mt={.5} display="flex" alignItems="center" fontWeight="bold">
                    <Typography mr={.5} fontSize={"large"} color={process.env.REACT_APP_COLOR_TEXT || "white"} >
                      {user?.result?.currentCredit}
                    </Typography>
                    <Typography fontSize={"small"} color={theme.palette.primary.main}>
                      {process.env.REACT_APP_CURRENCY_CODE}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => {
                        if (Date.now() < refreshCooldownUntilRef.current) {
                          Swal.fire({
                            title: t("header.refreshingtitle"),
                            text: t("header.refreshingbalance"),
                            icon: "info",
                          });
                          return;
                        }
                        refreshCooldownUntilRef.current = Date.now() + 5000;
                        dispatch(apiSlice.util.invalidateTags(["Profile"]));
                      }}
                    >
                      <Refresh sx={{ color: "#29A829", fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
                <Box display={"flex"} flexDirection={"row"}>
                  <Box mr={3} ml={0.5} display={"flex"} flexDirection={"column"} alignItems={"center"}
                    onClick={() => {
                      navigate(Constant.getPath("/deposit"), {
                        state: {
                          depositDetails: depositDetails?.result,
                        },
                      });
                    }}>
                    <Typography fontSize={"x-small"} color={process.env.REACT_APP_COLOR_TEXT} >
                      {i18n.t("walletcard.deposit")}
                    </Typography>
                    <Image
                      width="30px"
                      height="30px"
                      src={PATH + `/assets/img/wallet/wallet_card/DepositMobile.png`}
                    />
                  </Box>
                  <Box display={"flex"} flexDirection={"column"} alignItems={"center"}
                    onClick={() => {
                      navigate(Constant.getPath("/withdrawal"), {
                        state: {
                          withdrawDetails: withdrawDetails?.result,
                          user: user?.result,
                          // setSelectedScreen,
                        },
                      });
                    }}>
                    <Typography fontSize={"x-small"} color={process.env.REACT_APP_COLOR_TEXT}>
                      {i18n.t("walletcard.withdrawal")}
                    </Typography>

                    <Image
                      width="30px"
                      height="30px"
                      src={PATH + `/assets/img/wallet/wallet_card/Withdraw.png`}
                    />
                  </Box>
                </Box>
              </Box>

              <Box mt={1}>
                <ProfileList screens={screensMobile} />
              </Box >
            </Box >
          </Box >
        )}
      </Box >
    </>
  );
};

export default ProfileWallet;
